import type { MessengerLikeLikerPreview, MessengerMessage, MessengerPinnedMessage } from '@/api/client';
import { ApiError } from '@/api/client';
import { unwrapMessengerMessageSocketPayload, unwrapMessengerMessagesBatchSocketPayload } from '@/lib/messengerSocketPayload';
import { api } from '@/api/client';
import { getSocket } from '@/hooks/useRealtimeSync';
import { markUserRecentlyActive } from '@/lib/presenceStore';
import { invalidateMessengerUnread } from '@/constants/messengerEvents';
import { dismissMessengerThreadNotifications } from '@/notifications/notificationOrchestrator';
import { MESSENGER_OPTIMISTIC_ID_PREFIX } from '@/constants/messengerOptimistic';
import {
  MESSENGER_DIRECT_THREAD_CACHE_MAX_MESSAGES,
  MESSENGER_DIRECT_THREAD_CACHE_MAX_THREADS,
  MESSENGER_DIRECT_THREAD_PAGE_SIZE,
} from '@/components/Messenger/cache/directThreadCacheConstants';
import {
  appendIncomingDmMessage,
  mergeServerMessagesWithPendingOptimistic,
  patchMessageDeliveredAtInPlace,
  patchMessageReadAtInPlace,
} from '@/components/Messenger/cache/directThreadCacheMerge';
import { ackMessengerDelivery } from '@/components/Messenger/cache/messengerDeliveryAck';
import { normalizeMessengerMessage, normalizeLikeLikersPreview } from '@/utils/messengerMessageNormalize';
import { readMessengerImageFileDimensions } from '@/components/Messenger/shared/attachments/readMessengerImageFileDimensions';
import { isMessengerOptimisticMessageId } from '@/constants/messengerOptimistic';
import {
  effectiveHasMoreNewer,
  effectiveHasMoreOlder,
  hasNewerGapInWindow,
  mergeNewerPageIntoWindow,
  mergeOlderPageIntoWindow,
  resolveNewerPaginationCursor,
  resolveOlderPaginationCursor,
  trimDirectThreadMessageWindow,
  windowServerHeadId,
  windowServerTailId,
} from '@/components/Messenger/cache/directThreadWindow';

export type DirectThreadCacheSnapshot = {
  messages: MessengerMessage[];
  pinnedMessages: MessengerPinnedMessage[];
  /** @deprecated используйте hasMoreOlder */
  hasMore: boolean;
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  oldestLoadedMessageId: string | null;
  newestLoadedMessageId: string | null;
  lastReadMessageId: string | null;
  loading: boolean;
  loadingOlder: boolean;
  loadingNewer: boolean;
  sending: boolean;
  error: string | null;
  hydrated: boolean;
};

const EMPTY_SNAPSHOT: DirectThreadCacheSnapshot = Object.freeze({
  messages: [],
  pinnedMessages: [],
  hasMore: false,
  hasMoreOlder: false,
  hasMoreNewer: false,
  oldestLoadedMessageId: null,
  newestLoadedMessageId: null,
  lastReadMessageId: null,
  loading: false,
  loadingOlder: false,
  loadingNewer: false,
  sending: false,
  error: null,
  hydrated: false,
});

const DM_PAGINATION_COOLDOWN_MS = 450;
let paginationCooldownUntil = new Map<string, { older: number; newer: number }>();
/** FAB «к низу»: догрузка без промежуточного скролла/restore. */
const tailJumpActiveByThread = new Set<string>();

export function isDirectThreadTailJumpActive(threadId: string | null): boolean {
  if (!threadId) return false;
  return tailJumpActiveByThread.has(threadId);
}

function beginDirectThreadTailJump(threadId: string): void {
  tailJumpActiveByThread.add(threadId);
}

function endDirectThreadTailJump(threadId: string): void {
  tailJumpActiveByThread.delete(threadId);
}

type DirectThreadLoadNewerOptions = {
  bypassCooldown?: boolean;
};

function waitForThreadMetaFlag(
  threadId: string,
  flag: 'loading' | 'loadingNewer',
  timeoutMs = 15_000
): Promise<void> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const cur = entries.get(threadId);
      if (!cur || !cur[flag]) {
        resolve();
        return;
      }
      if (Date.now() - started > timeoutMs) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function createInitialEntry(): DirectThreadCacheSnapshot {
  return {
    messages: [],
    pinnedMessages: [],
    hasMore: false,
    hasMoreOlder: false,
    hasMoreNewer: false,
    oldestLoadedMessageId: null,
    newestLoadedMessageId: null,
    lastReadMessageId: null,
    loading: false,
    loadingOlder: false,
    loadingNewer: false,
    sending: false,
    error: null,
    hydrated: false,
  };
}

function snapshotWithLegacyHasMore(s: DirectThreadCacheSnapshot): DirectThreadCacheSnapshot {
  return { ...s, hasMore: s.hasMoreOlder };
}

type DirectThreadWindowMutation = 'prependOlder' | 'appendNewer' | 'realtimeTail' | 'initial';

function applyMessageListWindow(
  messages: MessengerMessage[],
  page: { hasMoreOlder: boolean; hasMoreNewer: boolean },
  mutation: DirectThreadWindowMutation
): Pick<DirectThreadCacheSnapshot, 'messages' | 'hasMoreOlder' | 'hasMoreNewer'> {
  let hasMoreOlder = page.hasMoreOlder;
  let hasMoreNewer = page.hasMoreNewer;
  let list = messages;

  const trimSide =
    mutation === 'prependOlder'
      ? 'end'
      : mutation === 'appendNewer' || mutation === 'realtimeTail'
        ? 'start'
        : 'end';

  if (list.length > MESSENGER_DIRECT_THREAD_CACHE_MAX_MESSAGES) {
    const trimmed = trimDirectThreadMessageWindow(list, MESSENGER_DIRECT_THREAD_CACHE_MAX_MESSAGES, trimSide);
    list = trimmed.messages;
    if (trimmed.trimmedFromEnd) hasMoreNewer = true;
    if (trimmed.trimmedFromStart) hasMoreOlder = true;
  }

  return { messages: list, hasMoreOlder, hasMoreNewer };
}

function logDmPaginationFailure(
  direction: 'older' | 'newer',
  threadId: string,
  cursorId: string | null,
  err: unknown
): void {
  console.warn('[messenger][dm-pagination]', {
    direction,
    threadId,
    cursorId,
    status: err instanceof ApiError ? err.status : undefined,
    message: err instanceof Error ? err.message : String(err),
  });
}

function isPaginationOnCooldown(threadId: string, direction: 'older' | 'newer'): boolean {
  const until = paginationCooldownUntil.get(threadId);
  if (!until) return false;
  return Date.now() < until[direction];
}

function setPaginationCooldown(threadId: string, direction: 'older' | 'newer'): void {
  const prev = paginationCooldownUntil.get(threadId) ?? { older: 0, newer: 0 };
  prev[direction] = Date.now() + DM_PAGINATION_COOLDOWN_MS;
  paginationCooldownUntil.set(threadId, prev);
}

function mergeSnapshotWithTailWindow(
  snapshot: DirectThreadCacheSnapshot,
  messages: MessengerMessage[]
): DirectThreadCacheSnapshot {
  const w = applyMessageListWindow(
    messages,
    { hasMoreOlder: snapshot.hasMoreOlder, hasMoreNewer: snapshot.hasMoreNewer },
    'realtimeTail'
  );
  const tailId = windowServerTailId(w.messages);
  return {
    ...snapshot,
    messages: w.messages,
    hasMoreOlder: w.hasMoreOlder,
    hasMoreNewer: w.hasMoreNewer,
    newestLoadedMessageId: tailId ?? snapshot.newestLoadedMessageId,
  };
}

let entries = new Map<string, DirectThreadCacheSnapshot>();

/** Количество параллельных отправок в треде — `sending` true, пока > 0. */
let sendInflightByThread = new Map<string, number>();

function bumpSendInflight(threadId: string): void {
  const n = (sendInflightByThread.get(threadId) ?? 0) + 1;
  sendInflightByThread.set(threadId, n);
}

function popSendInflight(threadId: string): void {
  const n = (sendInflightByThread.get(threadId) ?? 1) - 1;
  if (n <= 0) sendInflightByThread.delete(threadId);
  else sendInflightByThread.set(threadId, n);
}

function syncSendingFlag(threadId: string): void {
  const after = entries.get(threadId);
  if (!after) return;
  const active = (sendInflightByThread.get(threadId) ?? 0) > 0;
  if (after.sending === active) return;
  replaceEntry(threadId, { ...after, sending: active });
}

/** Якорь для восстановления позиции (Telegram-style): id сообщения + смещение от верха viewport. */
export type DirectThreadScrollAnchorPersist = {
  anchorMessageId: string;
  offsetFromViewportTop: number;
};

type CachedThreadScroll = {
  top: number;
  tailPinned: boolean;
  anchorMessageId?: string;
  offsetFromViewportTop?: number;
};

/** Резюме для ChatView: низ / якорь / только legacy scrollTop. */
export type DirectThreadScrollResume =
  | { kind: 'tail' }
  | {
      kind: 'anchor';
      anchorMessageId: string;
      offsetFromViewportTop: number;
      fallbackScrollTop: number;
    }
  | { kind: 'scrollTop'; top: number };

/** Позиция viewport; tailPinned = у низа — при возврате в чат не восстанавливаем top (докрут к хвосту). */
let scrollByThread = new Map<string, CachedThreadScroll>();
let listeners = new Map<string, Set<() => void>>();
/** Подписчики только на meta (loading/error/lastRead/hasMore/sending) без messages. */
let metaListeners = new Map<string, Set<() => void>>();
let protectedThreadId: string | null = null;
/** Текущий пользователь: для корректного liked_by_me в событии messenger:message_like */
let likeViewerPersonId: string | null = null;
/** Имя/аватар для оптимистичного превью лайкнувших (себя в стрипе сразу после лайка) */
let likeViewerSelfPreview: {
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
} | null = null;
let markReadDebounce = new Map<string, ReturnType<typeof setTimeout>>();

export function setDirectThreadLikeViewerPersonId(
  id: string | null,
  selfPreview?: { first_name?: string | null; last_name?: string | null; avatar_url?: string | null } | null
) {
  likeViewerPersonId = id;
  if (!id) {
    likeViewerSelfPreview = null;
  } else if (selfPreview !== undefined) {
    likeViewerSelfPreview = selfPreview;
  }
}

function emit(threadId: string) {
  const set = listeners.get(threadId);
  if (!set) return;
  set.forEach((cb) => cb());
}

function emitMeta(threadId: string) {
  const set = metaListeners.get(threadId);
  if (!set) return;
  set.forEach((cb) => cb());
}

function isContentChanged(
  prev: DirectThreadCacheSnapshot | undefined,
  next: DirectThreadCacheSnapshot
): boolean {
  if (!prev) return true;
  return (
    prev.messages !== next.messages ||
    prev.pinnedMessages !== next.pinnedMessages ||
    prev.hasMoreOlder !== next.hasMoreOlder ||
    prev.hasMoreNewer !== next.hasMoreNewer ||
    prev.oldestLoadedMessageId !== next.oldestLoadedMessageId ||
    prev.newestLoadedMessageId !== next.newestLoadedMessageId
  );
}

function isMetaChanged(
  prev: DirectThreadCacheSnapshot | undefined,
  next: DirectThreadCacheSnapshot
): boolean {
  if (!prev) return true;
  return (
    prev.loading !== next.loading ||
    prev.loadingOlder !== next.loadingOlder ||
    prev.loadingNewer !== next.loadingNewer ||
    prev.sending !== next.sending ||
    prev.error !== next.error ||
    prev.lastReadMessageId !== next.lastReadMessageId ||
    prev.hydrated !== next.hydrated
  );
}

function subscribeThread(threadId: string, cb: () => void) {
  let set = listeners.get(threadId);
  if (!set) {
    set = new Set();
    listeners.set(threadId, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
    if (set!.size === 0) listeners.delete(threadId);
  };
}

function evictIfNeeded() {
  while (entries.size > MESSENGER_DIRECT_THREAD_CACHE_MAX_THREADS) {
    let victim: string | undefined;
    for (const id of entries.keys()) {
      if (id !== protectedThreadId) {
        victim = id;
        break;
      }
    }
    if (!victim) break;
    const pendingRead = markReadDebounce.get(victim);
    if (pendingRead) clearTimeout(pendingRead);
    entries.delete(victim);
    scrollByThread.delete(victim);
    listeners.delete(victim);
    metaListeners.delete(victim);
    markReadDebounce.delete(victim);
    sendInflightByThread.delete(victim);
  }
}

/** Сбросить весь кэш (logout, смена пользователя). */
export function resetDirectThreadCache(): void {
  for (const timer of markReadDebounce.values()) clearTimeout(timer);
  entries = new Map();
  sendInflightByThread = new Map();
  scrollByThread = new Map();
  listeners = new Map();
  metaListeners = new Map();
  markReadDebounce = new Map();
  paginationCooldownUntil = new Map();
  protectedThreadId = null;
  likeViewerPersonId = null;
  likeViewerSelfPreview = null;
}

/** Вызывается из ChatShell: не вытесняем открытый тред из LRU. */
export function setDirectThreadCacheProtectedId(threadId: string | null) {
  protectedThreadId = threadId;
  evictIfNeeded();
}

function touchLru(threadId: string) {
  const e = entries.get(threadId) ?? createInitialEntry();
  entries.delete(threadId);
  entries.set(threadId, e);
  evictIfNeeded();
}

function replaceEntry(threadId: string, next: DirectThreadCacheSnapshot) {
  const normalized = snapshotWithLegacyHasMore(next);
  const prev = entries.get(threadId);
  const contentChanged = isContentChanged(prev, normalized);
  const metaChanged = isMetaChanged(prev, normalized);
  if (prev && !contentChanged && !metaChanged) return;

  entries.delete(threadId);
  entries.set(threadId, normalized);
  evictIfNeeded();
  if (contentChanged) emit(threadId);
  if (metaChanged) emitMeta(threadId);
}

function normalizePinnedMessages(raw: MessengerPinnedMessage[] | undefined): MessengerPinnedMessage[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((pin) => Boolean(pin?.message?.id))
    .map((pin) => ({
      ...pin,
      message: normalizeMessengerMessage({ ...pin.message, is_pinned: true }),
      pinned_by: String(pin.pinned_by ?? ''),
      pinned_by_name: String(pin.pinned_by_name ?? '').trim() || 'Участник',
      pinned_at: String(pin.pinned_at ?? ''),
    }));
}

function applyPinnedFlags(messages: MessengerMessage[], pins: MessengerPinnedMessage[]): MessengerMessage[] {
  if (pins.length === 0) {
    let needsClear = false;
    for (const msg of messages) {
      if (msg.is_pinned) {
        needsClear = true;
        break;
      }
    }
    if (!needsClear) return messages;
  }
  const pinnedIds = new Set(pins.map((pin) => pin.message.id));
  let changed = false;
  const next = messages.map((msg) => {
    const shouldBePinned = pinnedIds.has(msg.id);
    if (Boolean(msg.is_pinned) === shouldBePinned) return msg;
    changed = true;
    return normalizeMessengerMessage({ ...msg, is_pinned: shouldBePinned });
  });
  return changed ? next : messages;
}

function normalizeIncomingPage(
  raw: MessengerMessage[],
  pins: MessengerPinnedMessage[]
): MessengerMessage[] {
  if (raw.length === 0) return raw;
  return applyPinnedFlags(raw.map(normalizeMessengerMessage), pins);
}

function replacePinnedMessages(threadId: string, pinsRaw: MessengerPinnedMessage[]): void {
  const cur = entries.get(threadId);
  if (!cur) return;
  const pins = normalizePinnedMessages(pinsRaw);
  replaceEntry(threadId, {
    ...cur,
    pinnedMessages: pins,
    messages: applyPinnedFlags(cur.messages, pins),
  });
}

function mergeMessagesSortedByCreatedAt(
  current: MessengerMessage[],
  incoming: MessengerMessage[]
): MessengerMessage[] {
  const byId = new Map<string, MessengerMessage>();
  for (const msg of current) byId.set(msg.id, msg);
  for (const msg of incoming) byId.set(msg.id, msg);
  return [...byId.values()].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

function withEffectivePaginationFlags(s: DirectThreadCacheSnapshot): DirectThreadCacheSnapshot {
  return snapshotWithLegacyHasMore({
    ...s,
    hasMoreOlder: effectiveHasMoreOlder(s),
    hasMoreNewer: effectiveHasMoreNewer(s),
  });
}

export function getDirectThreadSnapshot(threadId: string | null): DirectThreadCacheSnapshot {
  if (!threadId) return EMPTY_SNAPSHOT;
  const raw = entries.get(threadId);
  if (!raw) return EMPTY_SNAPSHOT;
  return withEffectivePaginationFlags(raw);
}

export function subscribeDirectThread(threadId: string | null, onChange: () => void) {
  if (!threadId) return () => {};
  return subscribeThread(threadId, onChange);
}

function subscribeMetaThread(threadId: string, cb: () => void) {
  let set = metaListeners.get(threadId);
  if (!set) {
    set = new Set();
    metaListeners.set(threadId, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
    if (set!.size === 0) metaListeners.delete(threadId);
  };
}

/** Только meta-поля (loading, error, lastRead, …) — без messages/pins. */
export function subscribeDirectThreadMeta(threadId: string | null, onChange: () => void) {
  if (!threadId) return () => {};
  return subscribeMetaThread(threadId, onChange);
}

/** Слот под тред + перенос в конец LRU (открытие чата) без лишнего UI-сигнала. */
export function ensureDirectThreadSlot(threadId: string) {
  touchLru(threadId);
}

export function setDirectThreadCachedScrollTop(
  threadId: string,
  scrollTop: number,
  tailPinned: boolean,
  anchor?: DirectThreadScrollAnchorPersist | null
) {
  if (!entries.has(threadId)) return;
  if (tailPinned) {
    scrollByThread.set(threadId, { top: scrollTop, tailPinned: true });
    return;
  }
  if (anchor) {
    scrollByThread.set(threadId, {
      top: scrollTop,
      tailPinned: false,
      anchorMessageId: anchor.anchorMessageId,
      offsetFromViewportTop: anchor.offsetFromViewportTop,
    });
  } else {
    scrollByThread.set(threadId, { top: scrollTop, tailPinned: false });
  }
}

/**
 * Полное резюме позиции для DirectThreadView / ChatView.
 * null — нет записи (открываем как впервые → к хвосту).
 */
export function getDirectThreadScrollResume(threadId: string): DirectThreadScrollResume | null {
  if (!entries.has(threadId)) return null;
  const s = scrollByThread.get(threadId);
  if (!s) return null;
  if (s.tailPinned) return { kind: 'tail' };
  if (
    s.anchorMessageId != null &&
    s.offsetFromViewportTop != null &&
    Number.isFinite(s.offsetFromViewportTop)
  ) {
    return {
      kind: 'anchor',
      anchorMessageId: s.anchorMessageId,
      offsetFromViewportTop: s.offsetFromViewportTop,
      fallbackScrollTop: s.top,
    };
  }
  return { kind: 'scrollTop', top: s.top };
}

/**
 * Число для resumeScrollTop (fallback); undefined — к хвосту или якорь без legacy top.
 * @deprecated Предпочитайте getDirectThreadScrollResume.
 */
export function getDirectThreadCachedScrollTop(threadId: string): number | undefined {
  const r = getDirectThreadScrollResume(threadId);
  if (!r || r.kind === 'tail') return undefined;
  if (r.kind === 'anchor') return r.fallbackScrollTop;
  return r.top;
}

export function directThreadSetError(threadId: string, error: string | null) {
  const cur = entries.get(threadId);
  if (!cur) return;
  replaceEntry(threadId, { ...cur, error });
}

export async function directThreadLoadInitial(threadId: string, opts?: { force?: boolean }) {
  ensureDirectThreadSlot(threadId);
  const cur = entries.get(threadId);
  if (!cur || cur.loading) return;
  if (cur.hydrated && !opts?.force) return;
  replaceEntry(threadId, { ...cur, loading: true, error: null });
  try {
    const [res, pinsRes] = await Promise.all([
      api.messenger.getMessages(threadId, {
        limit: MESSENGER_DIRECT_THREAD_PAGE_SIZE,
      }),
      api.messenger.getPinnedMessages(threadId),
    ]);
    const after = entries.get(threadId);
    if (!after) return;
    const pinnedMessages = normalizePinnedMessages(pinsRes.pins);
    const serverNorm = applyPinnedFlags(res.messages.map(normalizeMessengerMessage), pinnedMessages);
    const merged = mergeServerMessagesWithPendingOptimistic(serverNorm, after.messages);
    const windowed = applyMessageListWindow(merged, res.page, 'initial');
    replaceEntry(threadId, {
      ...after,
      messages: windowed.messages,
      pinnedMessages,
      hasMoreOlder: windowed.hasMoreOlder,
      hasMoreNewer: windowed.hasMoreNewer,
      oldestLoadedMessageId: res.range.oldestLoadedMessageId,
      newestLoadedMessageId: res.range.newestLoadedMessageId,
      lastReadMessageId: res.last_read_message_id,
      loading: false,
      loadingOlder: false,
      loadingNewer: false,
      hydrated: true,
      error: null,
    });
  } catch (err) {
    const after = entries.get(threadId);
    if (!after) return;
    replaceEntry(threadId, { ...after, loading: false, error: (err as Error).message });
  }
}

export async function directThreadLoadOlder(threadId: string) {
  const cur = entries.get(threadId);
  if (!cur || !cur.hydrated || !effectiveHasMoreOlder(cur) || cur.loadingOlder || cur.loading) return;
  const cursorId = resolveOlderPaginationCursor(cur);
  if (!cursorId) return;
  if (isPaginationOnCooldown(threadId, 'older')) return;

  replaceEntry(threadId, { ...cur, loadingOlder: true, error: null });
  try {
    const res = await api.messenger.getMessages(threadId, {
      limit: MESSENGER_DIRECT_THREAD_PAGE_SIZE,
      before: cursorId,
    });
    const after = entries.get(threadId);
    if (!after) return;
    const incoming = normalizeIncomingPage(res.messages, after.pinnedMessages);
    const merged = mergeOlderPageIntoWindow(after.messages, incoming);
    const windowed = applyMessageListWindow(merged, res.page, 'prependOlder');
    replaceEntry(threadId, {
      ...after,
      messages: windowed.messages,
      hasMoreOlder: windowed.hasMoreOlder,
      hasMoreNewer: windowed.hasMoreNewer,
      oldestLoadedMessageId: res.range.oldestLoadedMessageId ?? cursorId,
      newestLoadedMessageId: after.newestLoadedMessageId,
      loadingOlder: false,
      error: null,
    });
    setPaginationCooldown(threadId, 'older');
  } catch (err) {
    logDmPaginationFailure('older', threadId, cursorId, err);
    const after = entries.get(threadId);
    if (!after) return;
    replaceEntry(threadId, {
      ...after,
      loadingOlder: false,
      error: (err as Error).message,
    });
    setPaginationCooldown(threadId, 'older');
  }
}

export async function directThreadLoadNewer(
  threadId: string,
  options?: DirectThreadLoadNewerOptions
): Promise<'loaded' | 'noop' | 'error'> {
  const cur = entries.get(threadId);
  if (!cur || !cur.hydrated || !effectiveHasMoreNewer(cur)) return 'noop';
  if (cur.loadingNewer || cur.loading) return 'noop';
  const cursorId = resolveNewerPaginationCursor(cur);
  if (!cursorId) return 'noop';
  if (!options?.bypassCooldown && isPaginationOnCooldown(threadId, 'newer')) return 'noop';

  replaceEntry(threadId, { ...cur, loadingNewer: true, error: null });
  try {
    const res = await api.messenger.getMessages(threadId, {
      limit: MESSENGER_DIRECT_THREAD_PAGE_SIZE,
      after: cursorId,
    });
    const after = entries.get(threadId);
    if (!after) return 'noop';
    const incoming = normalizeIncomingPage(res.messages, after.pinnedMessages);
    const merged = mergeNewerPageIntoWindow(after.messages, incoming);
    const windowed = applyMessageListWindow(merged, res.page, 'appendNewer');
    const hadGapBefore = hasNewerGapInWindow(after);
    let nextHasMoreNewer = windowed.hasMoreNewer;
    if (incoming.length === 0 && hadGapBefore) {
      nextHasMoreNewer = true;
    }
    const nextNewest =
      incoming.length > 0
        ? (res.range.newestLoadedMessageId ?? after.newestLoadedMessageId ?? cursorId)
        : after.newestLoadedMessageId;
    replaceEntry(threadId, {
      ...after,
      messages: windowed.messages,
      hasMoreOlder: windowed.hasMoreOlder,
      hasMoreNewer: nextHasMoreNewer,
      oldestLoadedMessageId: after.oldestLoadedMessageId,
      newestLoadedMessageId: nextNewest,
      loadingNewer: false,
      error: null,
    });
    if (!options?.bypassCooldown) {
      setPaginationCooldown(threadId, 'newer');
    }
    return 'loaded';
  } catch (err) {
    logDmPaginationFailure('newer', threadId, cursorId, err);
    const after = entries.get(threadId);
    if (!after) return 'error';
    replaceEntry(threadId, {
      ...after,
      loadingNewer: false,
      error: (err as Error).message,
    });
    if (!options?.bypassCooldown) {
      setPaginationCooldown(threadId, 'newer');
    }
    return 'error';
  }
}

/**
 * After WS reconnect / visibility: force after-cursor probe even when `hasMoreNewer === false`
 * (live tip would otherwise noop in `directThreadLoadNewer`). Mid-history windows merge only;
 * live tip with remaining newer pages jumps to the latest window.
 */
export async function directThreadCatchUpAfterReconnect(
  threadId: string
): Promise<'noop' | 'caught-up' | 'jumped' | 'error'> {
  let cur = entries.get(threadId);
  if (!cur || !cur.hydrated || !cur.newestLoadedMessageId) return 'noop';

  if (cur.loading) {
    await waitForThreadMetaFlag(threadId, 'loading');
    cur = entries.get(threadId);
    if (!cur || !cur.hydrated || !cur.newestLoadedMessageId) return 'noop';
  }
  if (cur.loadingNewer) {
    await waitForThreadMetaFlag(threadId, 'loadingNewer');
    cur = entries.get(threadId);
    if (!cur || !cur.hydrated || !cur.newestLoadedMessageId) return 'noop';
  }

  const wasAtLiveTip = !effectiveHasMoreNewer(cur);
  const cursorId = resolveNewerPaginationCursor(cur);
  if (!cursorId) return 'noop';

  replaceEntry(threadId, { ...cur, loadingNewer: true, error: null });
  try {
    const res = await api.messenger.getMessages(threadId, {
      limit: MESSENGER_DIRECT_THREAD_PAGE_SIZE,
      after: cursorId,
    });
    const after = entries.get(threadId);
    if (!after) return 'noop';
    const incoming = normalizeIncomingPage(res.messages, after.pinnedMessages);
    const merged = mergeNewerPageIntoWindow(after.messages, incoming);
    const windowed = applyMessageListWindow(merged, res.page, 'appendNewer');
    const hadGapBefore = hasNewerGapInWindow(after);
    let nextHasMoreNewer = windowed.hasMoreNewer;
    if (incoming.length === 0 && hadGapBefore) {
      nextHasMoreNewer = true;
    }
    const nextNewest =
      incoming.length > 0
        ? (res.range.newestLoadedMessageId ?? after.newestLoadedMessageId ?? cursorId)
        : after.newestLoadedMessageId;
    replaceEntry(threadId, {
      ...after,
      messages: windowed.messages,
      hasMoreOlder: windowed.hasMoreOlder,
      hasMoreNewer: nextHasMoreNewer,
      oldestLoadedMessageId: after.oldestLoadedMessageId,
      newestLoadedMessageId: nextNewest,
      loadingNewer: false,
      error: null,
    });

    if (incoming.length === 0) return 'caught-up';

    const afterMerge = entries.get(threadId);
    if (!afterMerge) return 'caught-up';
    if (wasAtLiveTip && effectiveHasMoreNewer(afterMerge)) {
      await directThreadJumpToTailWindow(threadId);
      return 'jumped';
    }
    return 'caught-up';
  } catch (err) {
    logDmPaginationFailure('newer', threadId, cursorId, err);
    const after = entries.get(threadId);
    if (!after) return 'error';
    replaceEntry(threadId, {
      ...after,
      loadingNewer: false,
      error: (err as Error).message,
    });
    return 'error';
  }
}

/** Mark other hydrated DMs as not-on-live-tip so they do not skip newer probes after reconnect. */
export function markOtherHydratedDirectThreadsHasMoreNewer(exceptThreadId: string | null): void {
  const except = exceptThreadId?.trim() || null;
  for (const [id, snap] of entries) {
    if (!snap.hydrated) continue;
    if (except && id === except) continue;
    if (snap.hasMoreNewer) continue;
    replaceEntry(id, { ...snap, hasMoreNewer: true });
  }
}

/**
 * FAB «к низу» (telegram-style): один запрос latest-page, полная замена окна,
 * без цепочки loadNewer по промежуточным страницам.
 */
export async function directThreadJumpToTailWindow(threadId: string): Promise<void> {
  ensureDirectThreadSlot(threadId);
  beginDirectThreadTailJump(threadId);
  try {
    setDirectThreadCachedScrollTop(threadId, 0, true);

    let snap = entries.get(threadId);
    if (snap?.loading) {
      await waitForThreadMetaFlag(threadId, 'loading');
    }
    snap = entries.get(threadId);
    if (snap?.loadingNewer) {
      await waitForThreadMetaFlag(threadId, 'loadingNewer');
    }

    await directThreadLoadInitial(threadId, { force: true });

    const after = entries.get(threadId);
    if (after?.error) {
      throw new Error(after.error);
    }
  } finally {
    endDirectThreadTailJump(threadId);
  }
}

/** @deprecated Используйте directThreadJumpToTailWindow */
export async function directThreadEnsureNewestTailLoaded(threadId: string): Promise<void> {
  await directThreadJumpToTailWindow(threadId);
}

export async function directThreadLoadMessageContext(
  threadId: string,
  messageId: string
): Promise<void> {
  ensureDirectThreadSlot(threadId);
  const cur = entries.get(threadId);
  if (!cur) return;
  if (cur.loading) return;
  if (cur.messages.some((m) => m.id === messageId)) return;

  replaceEntry(threadId, { ...cur, loading: true, error: null });
  try {
    const res = await api.messenger.getMessagesContext(threadId, {
      messageId,
      before: 30,
      after: 20,
    });
    const after = entries.get(threadId);
    if (!after) return;
    const serverNorm = applyPinnedFlags(res.messages.map(normalizeMessengerMessage), after.pinnedMessages);
    const merged = mergeMessagesSortedByCreatedAt(after.messages, serverNorm);
    const page = {
      hasMoreOlder: res.hasMore || after.hasMoreOlder,
      /** Окно context редко совпадает с live-tail; догрузка вниз через loadNewer. */
      hasMoreNewer: true,
    };
    const windowed = applyMessageListWindow(merged, page, 'initial');
    const nextMessages = windowed.messages.some((m) => m.id === messageId)
      ? windowed.messages
      : serverNorm;
    const rangeOldest = windowServerHeadId(nextMessages) ?? after.oldestLoadedMessageId;
    const rangeNewest = windowServerTailId(nextMessages) ?? after.newestLoadedMessageId;
    replaceEntry(threadId, {
      ...after,
      messages: nextMessages,
      hasMoreOlder: windowed.messages === nextMessages ? windowed.hasMoreOlder : page.hasMoreOlder,
      hasMoreNewer: windowed.hasMoreNewer,
      oldestLoadedMessageId: rangeOldest,
      newestLoadedMessageId: rangeNewest,
      lastReadMessageId: res.last_read_message_id,
      loading: false,
      hydrated: true,
      error: null,
    });
  } catch (err) {
    const after = entries.get(threadId);
    if (!after) return;
    replaceEntry(threadId, { ...after, loading: false, error: (err as Error).message });
  }
}

function createOptimisticMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${MESSENGER_OPTIMISTIC_ID_PREFIX}${crypto.randomUUID()}`;
  }
  return `${MESSENGER_OPTIMISTIC_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function patchMessageById(
  threadId: string,
  messageId: string,
  updater: (m: MessengerMessage) => MessengerMessage
) {
  const cur = entries.get(threadId);
  if (!cur) return;
  const idx = cur.messages.findIndex((m) => m.id === messageId);
  if (idx < 0) return;
  const cp = [...cur.messages];
  cp[idx] = updater(cp[idx]);
  replaceEntry(threadId, mergeSnapshotWithTailWindow(cur, cp));
}

function replaceOptimisticWithServerMessage(threadId: string, optimisticId: string, msg: MessengerMessage) {
  const after = entries.get(threadId);
  if (!after) return;
  const prev = after.messages;
  const i = prev.findIndex((m) => m.id === optimisticId);
  let nextMessages: MessengerMessage[];
  if (i < 0) {
    nextMessages = prev.some((m) => m.id === msg.id)
      ? prev
      : [...prev, normalizeMessengerMessage(msg)];
  } else {
    const cp = [...prev];
    const optimistic = cp[i]!;
    const stableBubbleKey = optimistic._client?.stableBubbleKey ?? optimistic.id;
    cp[i] = normalizeMessengerMessage({
      ...msg,
      image_width: msg.image_width ?? optimistic.image_width ?? null,
      image_height: msg.image_height ?? optimistic.image_height ?? null,
      _client: { ...msg._client, stableBubbleKey },
    });
    nextMessages = cp;
  }
  replaceEntry(threadId, { ...mergeSnapshotWithTailWindow(after, nextMessages), error: null });
}

/**
 * Локальные фото: сначала оптимистичный бабл с blob-превью и фазами uploading → posting,
 * затем загрузка на S3 и POST сообщения (подтверждение).
 */
export async function directThreadSendWithLocalImageFiles(
  threadId: string,
  currentUserId: string,
  params: {
    body?: string;
    context_attachments?: unknown;
    imageFiles: File[];
    reply_to_id?: string;
  }
): Promise<MessengerMessage | undefined> {
  const { body = '', context_attachments, imageFiles: rawFiles, reply_to_id } = params;
  const imageFiles = rawFiles.slice(0, 1);
  if (imageFiles.length === 0) {
    return directThreadSendMessage(threadId, currentUserId, { body, context_attachments, reply_to_id });
  }

  ensureDirectThreadSlot(threadId);
  bumpSendInflight(threadId);
  let lastMsg: MessengerMessage | undefined;
  try {
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i]!;
      const dims = await readMessengerImageFileDimensions(file);
      const optimisticId = createOptimisticMessageId();
      const objectUrl = URL.createObjectURL(file);
      const optimistic: MessengerMessage = {
        id: optimisticId,
        thread_id: threadId,
        sender_id: currentUserId,
        body: i === 0 ? body : '',
        file_url: objectUrl,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        image_width: dims?.width ?? null,
        image_height: dims?.height ?? null,
        context_attachments: i === 0 ? context_attachments : undefined,
        created_at: new Date().toISOString(),
        read_at: null,
        reply_to_id: i === 0 ? (reply_to_id ?? null) : null,
        _client: { attachmentPhase: 'uploading', stableBubbleKey: optimisticId },
      };
      const cur0 = entries.get(threadId);
      if (!cur0) {
        URL.revokeObjectURL(objectUrl);
        return undefined;
      }
      replaceEntry(threadId, {
        ...mergeSnapshotWithTailWindow(cur0, [...cur0.messages, optimistic]),
        sending: true,
        error: null,
      });

      try {
        const up = await api.messenger.uploadFile(file);
        patchMessageById(threadId, optimisticId, (m) => ({
          ...m,
          _client: { ...m._client, attachmentPhase: 'posting' },
        }));
        const msg = await api.messenger.sendMessage(threadId, {
          body: i === 0 ? body : '',
          file_url: up.url,
          file_key: up.key,
          file_name: up.file_name,
          file_size: up.file_size,
          mime_type: file.type?.trim() ? file.type : up.mime_type,
          ...(dims ? { image_width: dims.width, image_height: dims.height } : {}),
          ...(i === 0 && context_attachments !== undefined ? { context_attachments } : {}),
          ...(i === 0 && reply_to_id ? { reply_to_id } : {}),
        });
        URL.revokeObjectURL(objectUrl);
        replaceOptimisticWithServerMessage(threadId, optimisticId, msg);
        lastMsg = msg;
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        const after = entries.get(threadId);
        if (after) {
          replaceEntry(threadId, {
            ...after,
            messages: after.messages.filter((m) => m.id !== optimisticId),
            error: (err as Error).message,
          });
        }
        throw err;
      }
    }
    return lastMsg;
  } finally {
    popSendInflight(threadId);
    syncSendingFlag(threadId);
  }
}

export async function directThreadSendMessage(
  threadId: string,
  currentUserId: string,
  data: {
    body?: string;
    file_url?: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
    image_width?: number;
    image_height?: number;
    context_attachments?: unknown;
    reply_to_id?: string;
    forwarded_from_message_id?: string;
  }
) {
  ensureDirectThreadSlot(threadId);
  const cur = entries.get(threadId);
  if (!cur) return;
  bumpSendInflight(threadId);
  const optimisticId = createOptimisticMessageId();
  const optimistic: MessengerMessage = {
    id: optimisticId,
    thread_id: threadId,
    sender_id: currentUserId,
    body: data.body ?? '',
    file_url: data.file_url ?? null,
    file_name: data.file_name ?? null,
    file_size: data.file_size ?? null,
    mime_type: data.mime_type ?? null,
    image_width: data.image_width ?? null,
    image_height: data.image_height ?? null,
    context_attachments: data.context_attachments,
    created_at: new Date().toISOString(),
    read_at: null,
    reply_to_id: data.reply_to_id ?? null,
    forwarded_from_message_id: data.forwarded_from_message_id ?? null,
    _client: { stableBubbleKey: optimisticId },
  };
  replaceEntry(threadId, {
    ...mergeSnapshotWithTailWindow(cur, [...cur.messages, optimistic]),
    sending: true,
    error: null,
  });
  try {
    const msg = await api.messenger.sendMessage(threadId, data);
    replaceOptimisticWithServerMessage(threadId, optimisticId, msg);
    return msg;
  } catch (err) {
    const after = entries.get(threadId);
    if (after) {
      const failedMsg: MessengerMessage = {
        ...optimistic,
        _client: { status: 'failed', retryData: { ...data } },
      };
      const idx = after.messages.findIndex((m) => m.id === optimisticId);
      const updated = idx >= 0
        ? after.messages.map((m, i) => (i === idx ? failedMsg : m))
        : after.messages;
      replaceEntry(threadId, {
        ...after,
        messages: updated,
        error: (err as Error).message,
      });
    }
    throw err;
  } finally {
    popSendInflight(threadId);
    syncSendingFlag(threadId);
  }
}

/** Только поля тела POST `/messages` для пересылки (retryData без UI-полей). */
export type DirectThreadForwardSendPayload = {
  body?: string;
  file_url?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  image_width?: number;
  image_height?: number;
  context_attachments?: unknown;
  forwarded_from_message_id?: string;
  forwarded_from_label?: string;
};

/** Своё сообщение в атомарном batch (без полей пересылки). */
export type DirectThreadAtomicOwnPayload = {
  body?: string;
  context_attachments?: unknown;
  reply_to_id?: string;
  file_url?: string;
  file_key?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  image_width?: number;
  image_height?: number;
  /** Загрузка на S3 внутри atomic-отправки перед одним POST `/messages/batch` */
  localFile?: File;
};

function hasAtomicOwnContent(o: DirectThreadAtomicOwnPayload | undefined): boolean {
  if (!o) return false;
  if ((o.body ?? '').trim() !== '') return true;
  if (o.localFile) return true;
  if (o.file_url != null && String(o.file_url).trim() !== '' && String(o.file_url) !== 'null') return true;
  if (o.context_attachments != null && Array.isArray(o.context_attachments) && o.context_attachments.length > 0)
    return true;
  return Boolean(o.reply_to_id?.trim());
}

/**
 * Одна атомарная транзакция на сервере: своё сообщение (опционально) + пересланные.
 * Сокет-события рассылает сервер по одному на сообщение (как при отдельных POST).
 */
export async function directThreadSendOwnAndForwardedAtomic(
  threadId: string,
  currentUserId: string,
  args: {
    own?: DirectThreadAtomicOwnPayload;
    forwards: Array<{ apiPayload: DirectThreadForwardSendPayload; forwarded_from_sender_name: string }>;
  }
): Promise<MessengerMessage[]> {
  const { forwards } = args;
  if (forwards.length === 0) return [];

  let ownForApi: Record<string, unknown> | undefined;
  if (hasAtomicOwnContent(args.own)) {
    const o = args.own!;
    if (o.localFile) {
      const dims = await readMessengerImageFileDimensions(o.localFile);
      const up = await api.messenger.uploadFile(o.localFile);
      ownForApi = {
        body: o.body ?? '',
        file_url: up.url,
        file_key: up.key,
        file_name: up.file_name,
        file_size: up.file_size,
        mime_type: o.localFile.type?.trim() ? o.localFile.type : up.mime_type,
        ...(dims ? { image_width: dims.width, image_height: dims.height } : {}),
        ...(o.context_attachments !== undefined ? { context_attachments: o.context_attachments } : {}),
        ...(o.reply_to_id ? { reply_to_id: o.reply_to_id } : {}),
      };
    } else {
      ownForApi = {
        body: o.body ?? '',
        ...(o.file_url != null &&
        typeof o.file_url === 'string' &&
        o.file_url.trim() !== '' &&
        o.file_url !== 'null'
          ? {
              file_url: o.file_url,
              ...(o.file_key ? { file_key: o.file_key } : {}),
              ...(o.file_name != null ? { file_name: o.file_name } : {}),
              ...(o.file_size != null ? { file_size: o.file_size } : {}),
              ...(o.mime_type ? { mime_type: o.mime_type } : {}),
            }
          : {}),
        ...(o.image_width != null ? { image_width: o.image_width } : {}),
        ...(o.image_height != null ? { image_height: o.image_height } : {}),
        ...(o.context_attachments !== undefined ? { context_attachments: o.context_attachments } : {}),
        ...(o.reply_to_id ? { reply_to_id: o.reply_to_id } : {}),
      };
    }
  }

  ensureDirectThreadSlot(threadId);
  const cur = entries.get(threadId);
  if (!cur) return [];

  bumpSendInflight(threadId);
  const createdAt = new Date().toISOString();
  const optimisticIds: string[] = [];
  const optimistics: MessengerMessage[] = [];

  if (ownForApi) {
    const optimisticId = createOptimisticMessageId();
    optimisticIds.push(optimisticId);
    optimistics.push({
      id: optimisticId,
      thread_id: threadId,
      sender_id: currentUserId,
      body: String(ownForApi.body ?? ''),
      file_url: (ownForApi.file_url as string | null) ?? null,
      file_name: (ownForApi.file_name as string | null) ?? null,
      file_size: (ownForApi.file_size as number | null) ?? null,
      mime_type: (ownForApi.mime_type as string | null) ?? null,
      image_width: (ownForApi.image_width as number | null) ?? null,
      image_height: (ownForApi.image_height as number | null) ?? null,
      context_attachments: ownForApi.context_attachments as MessengerMessage['context_attachments'],
      created_at: createdAt,
      read_at: null,
      reply_to_id: (ownForApi.reply_to_id as string | null) ?? null,
      forwarded_from_message_id: null,
      forwarded_from_label: null,
      _client: { stableBubbleKey: optimisticId },
    });
  }

  for (const item of forwards) {
    const data = item.apiPayload;
    const optimisticId = createOptimisticMessageId();
    optimisticIds.push(optimisticId);
    optimistics.push({
      id: optimisticId,
      thread_id: threadId,
      sender_id: currentUserId,
      body: data.body ?? '',
      file_url: data.file_url ?? null,
      file_name: data.file_name ?? null,
      file_size: data.file_size ?? null,
      mime_type: data.mime_type ?? null,
      image_width: data.image_width ?? null,
      image_height: data.image_height ?? null,
      context_attachments: data.context_attachments as MessengerMessage['context_attachments'],
      created_at: createdAt,
      read_at: null,
      reply_to_id: null,
      forwarded_from_message_id: data.forwarded_from_message_id ?? null,
      forwarded_from_label: data.forwarded_from_label ?? null,
      forwarded_from_sender_name: item.forwarded_from_sender_name,
      _client: { stableBubbleKey: optimisticId },
    });
  }

  replaceEntry(threadId, {
    ...mergeSnapshotWithTailWindow(cur, [...cur.messages, ...optimistics]),
    sending: true,
    error: null,
  });

  try {
    const { messages } = await api.messenger.sendMessagesAtomicBatch(threadId, {
      ...(ownForApi ? { own: ownForApi } : {}),
      forwards: forwards.map((f) => f.apiPayload),
    });
    if (!Array.isArray(messages) || messages.length !== optimisticIds.length) {
      throw new Error('Сервер вернул некорректный ответ batch');
    }
    for (let i = 0; i < optimisticIds.length; i++) {
      replaceOptimisticWithServerMessage(threadId, optimisticIds[i]!, messages[i]!);
    }
    return messages;
  } catch (err) {
    const after = entries.get(threadId);
    if (after) {
      const idSet = new Set(optimisticIds);
      replaceEntry(threadId, {
        ...after,
        messages: after.messages.filter((m) => !idSet.has(m.id)),
        error: (err as Error).message,
      });
    }
    throw err;
  } finally {
    popSendInflight(threadId);
    syncSendingFlag(threadId);
  }
}

/**
 * Все пересланные сообщения сразу в ленте (один replaceEntry), затем по очереди POST.
 * При падении одного — помечаем failed с retry, остальные всё равно отправляем; в конце бросаем первую ошибку для отката композера.
 */
export async function directThreadSendForwardedMessagesBatch(
  threadId: string,
  currentUserId: string,
  items: Array<{
    apiPayload: DirectThreadForwardSendPayload;
    forwarded_from_sender_name: string;
  }>
): Promise<MessengerMessage | undefined> {
  if (items.length === 0) return;
  ensureDirectThreadSlot(threadId);
  const cur = entries.get(threadId);
  if (!cur) return;

  bumpSendInflight(threadId);
  const createdAt = new Date().toISOString();
  const zipped = items.map((item) => {
    const data = item.apiPayload;
    const optimisticId = createOptimisticMessageId();
    const optimistic: MessengerMessage = {
      id: optimisticId,
      thread_id: threadId,
      sender_id: currentUserId,
      body: data.body ?? '',
      file_url: data.file_url ?? null,
      file_name: data.file_name ?? null,
      file_size: data.file_size ?? null,
      mime_type: data.mime_type ?? null,
      image_width: data.image_width ?? null,
      image_height: data.image_height ?? null,
      context_attachments: data.context_attachments as MessengerMessage['context_attachments'],
      created_at: createdAt,
      read_at: null,
      reply_to_id: null,
      forwarded_from_message_id: data.forwarded_from_message_id ?? null,
      forwarded_from_label: data.forwarded_from_label ?? null,
      forwarded_from_sender_name: item.forwarded_from_sender_name,
      _client: {
        stableBubbleKey: optimisticId,
      },
    };
    return { optimisticId, optimistic, apiPayload: item.apiPayload };
  });

  replaceEntry(threadId, {
    ...mergeSnapshotWithTailWindow(cur, [...cur.messages, ...zipped.map((z) => z.optimistic)]),
    sending: true,
    error: null,
  });

  let firstError: Error | undefined;
  let lastMsg: MessengerMessage | undefined;
  try {
    for (const { optimisticId, optimistic, apiPayload } of zipped) {
      try {
        const msg = await api.messenger.sendMessage(threadId, apiPayload);
        replaceOptimisticWithServerMessage(threadId, optimisticId, msg);
        lastMsg = msg;
      } catch (err) {
        const e = err as Error;
        if (!firstError) firstError = e;
        const after = entries.get(threadId);
        if (after) {
          const failedMsg: MessengerMessage = {
            ...optimistic,
            _client: { status: 'failed', retryData: { ...apiPayload } },
          };
          const idx = after.messages.findIndex((m) => m.id === optimisticId);
          const updated =
            idx >= 0 ? after.messages.map((m, i) => (i === idx ? failedMsg : m)) : after.messages;
          replaceEntry(threadId, {
            ...after,
            messages: updated,
            error: e.message,
          });
        }
      }
    }
    if (firstError) throw firstError;
    return lastMsg;
  } finally {
    popSendInflight(threadId);
    syncSendingFlag(threadId);
  }
}

export async function directThreadRetryMessage(
  threadId: string,
  _currentUserId: string,
  optimisticId: string
): Promise<void> {
  const cur = entries.get(threadId);
  if (!cur) return;
  const failedMsg = cur.messages.find((m) => m.id === optimisticId);
  if (!failedMsg || failedMsg._client?.status !== 'failed' || !failedMsg._client.retryData) return;

  const retryData = failedMsg._client.retryData;
  // Сбросить статус failed — вернуть в состояние "отправляется"
  patchMessageById(threadId, optimisticId, (m) => ({
    ...m,
    _client: undefined,
  }));

  bumpSendInflight(threadId);
  const after0 = entries.get(threadId);
  if (after0) replaceEntry(threadId, { ...after0, sending: true, error: null });

  try {
    const msg = await api.messenger.sendMessage(threadId, retryData);
    replaceOptimisticWithServerMessage(threadId, optimisticId, msg);
  } catch (err) {
    const after = entries.get(threadId);
    if (after) {
      const retried: MessengerMessage = {
        ...failedMsg,
        _client: { status: 'failed', retryData },
      };
      const idx = after.messages.findIndex((m) => m.id === optimisticId);
      const updated = idx >= 0
        ? after.messages.map((m, i) => (i === idx ? retried : m))
        : after.messages;
      replaceEntry(threadId, {
        ...after,
        messages: updated,
        error: (err as Error).message,
      });
    }
    throw err;
  } finally {
    popSendInflight(threadId);
    syncSendingFlag(threadId);
  }
}

export function directThreadMarkReadToBottom(threadId: string, messageId: string) {
  const cur = entries.get(threadId);
  if (!cur) return;
  if (cur.lastReadMessageId === messageId) return;
  const list = cur.messages;
  const newIdx = list.findIndex((m) => m.id === messageId);
  if (newIdx < 0) return;
  const prevRead = cur.lastReadMessageId;
  if (prevRead) {
    const prevIdx = list.findIndex((m) => m.id === prevRead);
    if (prevIdx >= 0 && newIdx < prevIdx) return;
  }
  replaceEntry(threadId, { ...cur, lastReadMessageId: messageId });
  const existing = markReadDebounce.get(threadId);
  if (existing) clearTimeout(existing);
  markReadDebounce.set(
    threadId,
    setTimeout(() => {
      markReadDebounce.delete(threadId);
      void api.messenger
        .markRead(threadId, messageId)
        .then(() => {
          dismissMessengerThreadNotifications(threadId);
          invalidateMessengerUnread();
        })
        .catch(() => {});
    }, 300)
  );
}

function applyIncomingMessagesBatch(msgs: MessengerMessage[]) {
  if (msgs.length === 0) return;
  const ordered = [...msgs].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const normalized = ordered.map((m) => normalizeMessengerMessage(m));
  const tid = normalized[0]!.thread_id;
  if (!tid || !normalized.every((m) => m.thread_id === tid)) return;
  if (!entries.has(tid)) return;
  const cur = entries.get(tid)!;
  const normalizedWithPins = applyPinnedFlags(normalized, cur.pinnedMessages);
  let nextList = cur.messages;
  for (const m of normalizedWithPins) {
    const n = appendIncomingDmMessage(nextList, m);
    if (n !== nextList) nextList = n;
  }
  if (nextList === cur.messages) return;
  replaceEntry(tid, mergeSnapshotWithTailWindow(cur, nextList));
  if (likeViewerPersonId) {
    const toAck = normalizedWithPins
      .filter((m) => m.sender_id !== likeViewerPersonId && !m.deleted_at)
      .map((m) => m.id);
    if (toAck.length > 0) ackMessengerDelivery(toAck);
  }
}

function applyIncomingMessage(msg: MessengerMessage) {
  const normalized = normalizeMessengerMessage(msg);
  const tid = normalized.thread_id;
  if (!entries.has(tid)) return;
  const cur = entries.get(tid)!;
  const [normalizedWithPins] = applyPinnedFlags([normalized], cur.pinnedMessages);
  const nextList = appendIncomingDmMessage(cur.messages, normalizedWithPins ?? normalized);
  if (nextList === cur.messages) return;
  replaceEntry(tid, mergeSnapshotWithTailWindow(cur, nextList));
  if (likeViewerPersonId && normalized.sender_id !== likeViewerPersonId && !normalized.deleted_at) {
    ackMessengerDelivery(normalized.id);
  }
}

function applyReadReceipt(messageId: string) {
  if (!messageId) return;
  for (const [tid, cur] of entries) {
    const next = patchMessageReadAtInPlace(cur.messages, messageId);
    if (!next) continue;
    replaceEntry(tid, { ...cur, messages: next });
    break;
  }
}

function applyDeliveredReceipt(messageId: string, deliveredAt?: string | null) {
  if (!messageId) return;
  for (const [tid, cur] of entries) {
    const next = patchMessageDeliveredAtInPlace(cur.messages, messageId, deliveredAt);
    if (!next) continue;
    replaceEntry(tid, { ...cur, messages: next });
    break;
  }
}

function applyMessageDeleted(messageId: string, threadId: string) {
  const cur = entries.get(threadId);
  if (!cur) return;
  const idx = cur.messages.findIndex((m) => m.id === messageId);
  if (idx < 0) return;
  const deletedAt = new Date().toISOString();
  const cp = [...cur.messages];
  cp[idx] = {
    ...cp[idx],
    deleted_at: deletedAt,
    body: '',
    file_url: null,
    file_name: null,
    file_size: null,
    mime_type: null,
    image_width: null,
    image_height: null,
    context_attachments: undefined,
    is_pinned: false,
  };
  const cascaded = refreshReplySnapshotsForDeletedMessage(cp, messageId);
  replaceEntry(threadId, {
    ...cur,
    messages: cascaded,
    pinnedMessages: cur.pinnedMessages.filter((pin) => pin.message.id !== messageId),
  });
}

function applyMessageRemoved(messageId: string, threadId: string) {
  const cur = entries.get(threadId);
  if (!cur) return;
  const cp = cur.messages.filter((m) => m.id !== messageId);
  const cascaded = refreshReplySnapshotsForDeletedMessage(cp, messageId);
  replaceEntry(threadId, {
    ...cur,
    messages: cascaded,
    pinnedMessages: cur.pinnedMessages.filter((pin) => pin.message.id !== messageId),
  });
}

function insertMessageSortedByCreatedAt(list: MessengerMessage[], msg: MessengerMessage): MessengerMessage[] {
  const ts = new Date(msg.created_at).getTime();
  let i = 0;
  const n = list.length;
  while (i < n && new Date(list[i]!.created_at).getTime() <= ts) i += 1;
  return [...list.slice(0, i), msg, ...list.slice(i)];
}

/**
 * Optimistic delete + API call. В «Сохранённых» — строка убирается из кэша (сервер DELETE).
 * Если сервер вернёт ошибку — откатить optimistic-патч.
 */
export async function directThreadDeleteMessage(
  threadId: string,
  messageId: string,
  opts?: { hardRemove?: boolean }
): Promise<void> {
  const cur = entries.get(threadId);
  if (!cur) return;
  const original = cur.messages.find((m) => m.id === messageId);

  if (opts?.hardRemove) {
    applyMessageRemoved(messageId, threadId);
  } else {
    applyMessageDeleted(messageId, threadId);
  }

  try {
    await api.messenger.deleteMessage(threadId, messageId);
  } catch (err) {
    if (original) {
      const after = entries.get(threadId);
      if (after) {
        if (opts?.hardRemove) {
          const merged = insertMessageSortedByCreatedAt(
            after.messages.filter((m) => m.id !== messageId),
            original
          );
          const rollbackReplies = refreshReplySnapshotsForRestoredParent(merged, messageId, original);
          replaceEntry(threadId, {
            ...after,
            messages: rollbackReplies,
            error: (err as Error).message,
          });
        } else {
          const idx = after.messages.findIndex((m) => m.id === messageId);
          if (idx >= 0) {
            const restored = [...after.messages];
            restored[idx] = original;
            const rollbackReplies = refreshReplySnapshotsForRestoredParent(
              restored,
              messageId,
              original
            );
            replaceEntry(threadId, {
              ...after,
              messages: rollbackReplies,
              error: (err as Error).message,
            });
          }
        }
      }
    }
    throw err;
  }
}

/** Текст родителя изменился — обновить снимок `reply_to` во всех ответах за один проход. */
function refreshReplySnapshotsForEditedMessage(
  messages: MessengerMessage[],
  editedMessageId: string,
  newBody: string
): MessengerMessage[] {
  return messages.map((m) =>
    m.reply_to?.id === editedMessageId
      ? { ...m, reply_to: { ...m.reply_to, body: newBody } }
      : m
  );
}

/** Родитель удалён — очистить снимок `reply_to` у всех ответов (как в SQL при rt.deleted_at). */
function refreshReplySnapshotsForDeletedMessage(
  messages: MessengerMessage[],
  deletedMessageId: string
): MessengerMessage[] {
  return messages.map((m) =>
    m.reply_to?.id === deletedMessageId
      ? {
          ...m,
          reply_to: {
            ...m.reply_to,
            body: '',
            file_name: null,
            file_url: null,
            mime_type: null,
            context_attachments: null,
            forwarded_from_sender_name: null,
          },
        }
      : m
  );
}

/** Откат удаления: восстановить поля снимка из снова актуального родителя. */
function refreshReplySnapshotsForRestoredParent(
  messages: MessengerMessage[],
  parentId: string,
  parent: MessengerMessage
): MessengerMessage[] {
  const body = parent.deleted_at ? '' : (parent.body ?? '');
  const cleared = Boolean(parent.deleted_at);
  return messages.map((m) => {
    if (m.reply_to?.id !== parentId) return m;
    const snap = m.reply_to;
    return {
      ...m,
      reply_to: {
        ...snap,
        body,
        file_name: cleared ? null : parent.file_name ?? null,
        file_url: cleared ? null : parent.file_url ?? null,
        mime_type: cleared ? null : parent.mime_type ?? null,
        context_attachments: cleared ? null : parent.context_attachments ?? null,
        forwarded_from_sender_name: cleared
          ? null
          : parent.forwarded_from_sender_name ?? null,
      },
    };
  });
}

function applyMessageEdited(msg: MessengerMessage) {
  const normalized = normalizeMessengerMessage(msg);
  const cur = entries.get(normalized.thread_id);
  if (!cur) return;
  const idx = cur.messages.findIndex((m) => m.id === normalized.id);
  if (idx < 0) return;
  const cp = [...cur.messages];
  cp[idx] = { ...cp[idx], body: normalized.body, edited_at: normalized.edited_at };
  const cascaded = refreshReplySnapshotsForEditedMessage(cp, normalized.id, normalized.body);
  replaceEntry(normalized.thread_id, mergeSnapshotWithTailWindow(cur, cascaded));
}

/**
 * Optimistic edit + API call.
 * Если сервер вернёт ошибку — откатить оригинал.
 */
export async function directThreadEditMessage(
  threadId: string,
  messageId: string,
  body: string
): Promise<void> {
  const cur = entries.get(threadId);
  if (!cur) return;

  const original = cur.messages.find((m) => m.id === messageId);
  if (!original) return;

  // Optimistic: само сообщение + цитаты в ответах — одна атомарная замена списка
  const idx = cur.messages.findIndex((m) => m.id === messageId);
  const cp = [...cur.messages];
  cp[idx] = { ...cp[idx], body, edited_at: new Date().toISOString() };
  const optimisticCascaded = refreshReplySnapshotsForEditedMessage(cp, messageId, body);
  replaceEntry(threadId, mergeSnapshotWithTailWindow(cur, optimisticCascaded));

  try {
    const updated = await api.messenger.editMessage(threadId, messageId, body);
    const after = entries.get(threadId);
    if (!after) return;
    const i = after.messages.findIndex((m) => m.id === messageId);
    if (i < 0) return;
    const prevRow = after.messages[i]!;
    const normalized = normalizeMessengerMessage(updated);
    const merged = { ...prevRow, ...normalized, _client: prevRow._client };
    const next = [...after.messages];
    next[i] = merged;
    const serverCascaded = refreshReplySnapshotsForEditedMessage(next, messageId, merged.body);
    replaceEntry(threadId, mergeSnapshotWithTailWindow(after, serverCascaded));
  } catch (err) {
    const after = entries.get(threadId);
    if (after) {
      const i = after.messages.findIndex((m) => m.id === messageId);
      if (i >= 0) {
        const restored = [...after.messages];
        restored[i] = original;
        const rollbackCascaded = refreshReplySnapshotsForEditedMessage(
          restored,
          messageId,
          original.body ?? ''
        );
        replaceEntry(threadId, {
          ...mergeSnapshotWithTailWindow(after, rollbackCascaded),
          error: (err as Error).message,
        });
      }
    }
    throw err;
  }
}

type MessengerMessageLikeSocketPayload = {
  threadId: string;
  messageId: string;
  likeCount: number;
  actorPersonId: string;
  action: 'add' | 'remove';
  /** Если пришло с сервера — атомарно с счётчиком; иначе undefined (не затираем превью) */
  likeLikersPreview?: MessengerLikeLikerPreview[];
};

/** Сокет может отдать camelCase/snake_case и число как string — нормализуем один раз. */
function parseMessengerMessageLikeSocketPayload(raw: unknown): MessengerMessageLikeSocketPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const threadId = String(o.threadId ?? o.thread_id ?? '').trim();
  const messageId = String(o.messageId ?? o.message_id ?? '').trim();
  if (!threadId || !messageId) return null;

  const lcRaw = o.likeCount ?? o.like_count;
  let likeCount = 0;
  if (typeof lcRaw === 'number' && Number.isFinite(lcRaw)) {
    likeCount = Math.max(0, Math.trunc(lcRaw));
  } else if (lcRaw != null && lcRaw !== '') {
    const n = Number(lcRaw);
    if (Number.isFinite(n)) likeCount = Math.max(0, Math.trunc(n));
  }

  const actorPersonId = String(o.actorPersonId ?? o.actor_person_id ?? '').trim();
  const action: 'add' | 'remove' = o.action === 'remove' ? 'remove' : 'add';

  let likeLikersPreview: MessengerLikeLikerPreview[] | undefined;
  if (o.likeLikersPreview !== undefined && o.likeLikersPreview !== null) {
    likeLikersPreview = normalizeLikeLikersPreview(o.likeLikersPreview);
  } else if (o.like_likers_preview !== undefined && o.like_likers_preview !== null) {
    likeLikersPreview = normalizeLikeLikersPreview(o.like_likers_preview);
  }

  return { threadId, messageId, likeCount, actorPersonId, action, likeLikersPreview };
}

function applyMessageLikeFromSocket(raw: unknown) {
  const payload = parseMessengerMessageLikeSocketPayload(raw);
  if (!payload) return;
  const { threadId, messageId, likeCount, actorPersonId, action, likeLikersPreview } = payload;
  const cur = entries.get(threadId);
  if (!cur) return;
  const mid = messageId.toLowerCase();
  const idx = cur.messages.findIndex((m) => m.id.toLowerCase() === mid);
  if (idx < 0) return;
  const cp = [...cur.messages];
  const m = cp[idx]!;
  const viewer = likeViewerPersonId;
  let likedByMe = Boolean(m.liked_by_me);
  if (viewer && actorPersonId && viewer === actorPersonId) {
    likedByMe = action === 'add';
  }
  cp[idx] = normalizeMessengerMessage({
    ...m,
    like_count: likeCount,
    liked_by_me: likedByMe,
    like_likers_preview: likeLikersPreview !== undefined ? likeLikersPreview : m.like_likers_preview,
  });
  replaceEntry(threadId, mergeSnapshotWithTailWindow(cur, cp));
}

/** Переключить лайк текущего пользователя (оптимистично, с откатом при ошибке). */
export async function directThreadToggleMessageLike(threadId: string, messageId: string): Promise<void> {
  if (!likeViewerPersonId) return;
  const cur = entries.get(threadId);
  if (!cur) return;
  const idx = cur.messages.findIndex((m) => m.id === messageId);
  if (idx < 0) return;
  const m = cur.messages[idx]!;
  if (m.deleted_at) return;
  if (isMessengerOptimisticMessageId(m.id)) return;

  const prevCount = m.like_count ?? 0;
  const prevLiked = Boolean(m.liked_by_me);
  const prevLikers = [...(m.like_likers_preview ?? [])];
  const wantLike = !prevLiked;
  const optimisticCount = wantLike ? prevCount + 1 : Math.max(0, prevCount - 1);
  const optimisticLiked = wantLike;
  const withoutSelf = likeViewerPersonId
    ? prevLikers.filter((x) => String(x.person_id) !== String(likeViewerPersonId))
    : [...prevLikers];
  let optimisticLikers: MessengerLikeLikerPreview[] = withoutSelf;
  if (wantLike && likeViewerPersonId) {
    const self: MessengerLikeLikerPreview = {
      person_id: likeViewerPersonId,
      first_name: likeViewerSelfPreview?.first_name ?? null,
      last_name: likeViewerSelfPreview?.last_name ?? null,
      avatar_url: likeViewerSelfPreview?.avatar_url ?? null,
    };
    optimisticLikers = [self, ...withoutSelf].slice(0, 3);
  }

  const cpOpt = [...cur.messages];
  cpOpt[idx] = normalizeMessengerMessage({
    ...m,
    like_count: optimisticCount,
    liked_by_me: optimisticLiked,
    like_likers_preview: optimisticLikers,
  });
  replaceEntry(threadId, mergeSnapshotWithTailWindow(cur, cpOpt));

  try {
    const r = wantLike
      ? await api.messenger.setMessageLike(threadId, messageId)
      : await api.messenger.removeMessageLike(threadId, messageId);
    const after = entries.get(threadId);
    if (!after) return;
    const i2 = after.messages.findIndex((x) => x.id === messageId);
    if (i2 < 0) return;
    const row = after.messages[i2]!;
    const next = [...after.messages];
    next[i2] = normalizeMessengerMessage({
      ...row,
      like_count: r.like_count,
      liked_by_me: r.liked_by_me,
      like_likers_preview: r.like_likers_preview ?? row.like_likers_preview,
    });
    replaceEntry(threadId, mergeSnapshotWithTailWindow(after, next));
  } catch {
    const after = entries.get(threadId);
    if (!after) return;
    const i3 = after.messages.findIndex((x) => x.id === messageId);
    if (i3 < 0) return;
    const rollback = [...after.messages];
    rollback[i3] = normalizeMessengerMessage({
      ...rollback[i3]!,
      like_count: prevCount,
      liked_by_me: prevLiked,
      like_likers_preview: prevLikers,
    });
    replaceEntry(threadId, mergeSnapshotWithTailWindow(after, rollback));
  }
}

export async function directThreadPinMessage(threadId: string, messageId: string): Promise<void> {
  const cur = entries.get(threadId);
  if (!cur) return;
  const msg = cur.messages.find((m) => m.id === messageId);
  if (!msg || msg.deleted_at || msg.message_kind === 'system' || isMessengerOptimisticMessageId(msg.id)) return;

  try {
    const res = await api.messenger.pinMessage(threadId, messageId);
    replacePinnedMessages(threadId, res.pins);
  } catch (err) {
    const after = entries.get(threadId);
    if (after) replaceEntry(threadId, { ...after, error: (err as Error).message });
    throw err;
  }
}

export async function directThreadUnpinMessage(threadId: string, messageId: string): Promise<void> {
  const cur = entries.get(threadId);
  if (!cur) return;

  try {
    const res = await api.messenger.unpinMessage(threadId, messageId);
    replacePinnedMessages(threadId, res.pins);
  } catch (err) {
    const after = entries.get(threadId);
    if (after) replaceEntry(threadId, { ...after, error: (err as Error).message });
    throw err;
  }
}

type MessengerPinsUpdatedSocketPayload = {
  threadId: string;
  pins: MessengerPinnedMessage[];
};

function parseMessengerPinsUpdatedSocketPayload(raw: unknown): MessengerPinsUpdatedSocketPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const threadId = String(o.threadId ?? o.thread_id ?? '').trim();
  if (!threadId || !Array.isArray(o.pins)) return null;
  return { threadId, pins: o.pins as MessengerPinnedMessage[] };
}

function applyPinsUpdatedFromSocket(raw: unknown) {
  const payload = parseMessengerPinsUpdatedSocketPayload(raw);
  if (!payload) return;
  replacePinnedMessages(payload.threadId, payload.pins);
}

export function installDirectThreadSocketBridge(): () => void {
  const socket = getSocket();
  const onNewMessage = (raw: unknown) => {
    const { message: msg } = unwrapMessengerMessageSocketPayload(raw);
    if (msg.sender_id && msg.sender_id !== likeViewerPersonId) {
      markUserRecentlyActive(msg.sender_id, msg.created_at);
    }
    applyIncomingMessage(msg);
  };
  const onRead = (data: { messageId?: string }) => {
    applyReadReceipt(data?.messageId ?? '');
  };
  const onDelivered = (data: { messageId?: string; deliveredAt?: string }) => {
    applyDeliveredReceipt(data?.messageId ?? '', data?.deliveredAt);
  };
  const onMessageDeleted = (data: { messageId?: string; threadId?: string; permanent?: boolean }) => {
    if (data?.messageId && data?.threadId) {
      if (data.permanent) {
        applyMessageRemoved(data.messageId, data.threadId);
      } else {
        applyMessageDeleted(data.messageId, data.threadId);
      }
    }
  };
  const onMessageEdited = (msg: MessengerMessage) => {
    applyMessageEdited(msg);
  };
  const onMessageLike = (data: unknown) => {
    applyMessageLikeFromSocket(data);
  };
  const onPinsUpdated = (data: unknown) => {
    applyPinsUpdatedFromSocket(data);
  };
  const onMessagesBatch = (raw: unknown) => {
    const parsed = unwrapMessengerMessagesBatchSocketPayload(raw);
    if (!parsed) return;
    for (const msg of parsed.messages) {
      if (msg.sender_id && msg.sender_id !== likeViewerPersonId) {
        markUserRecentlyActive(msg.sender_id, msg.created_at);
      }
    }
    applyIncomingMessagesBatch(parsed.messages);
  };
  socket.on('messenger:message', onNewMessage);
  socket.on('messenger:messages_batch', onMessagesBatch);
  socket.on('messenger:read', onRead);
  socket.on('messenger:delivered', onDelivered);
  socket.on('messenger:message_deleted', onMessageDeleted);
  socket.on('messenger:message_edited', onMessageEdited);
  socket.on('messenger:message_like', onMessageLike);
  socket.on('messenger:pins_updated', onPinsUpdated);
  return () => {
    socket.off('messenger:message', onNewMessage);
    socket.off('messenger:messages_batch', onMessagesBatch);
    socket.off('messenger:read', onRead);
    socket.off('messenger:delivered', onDelivered);
    socket.off('messenger:message_deleted', onMessageDeleted);
    socket.off('messenger:message_edited', onMessageEdited);
    socket.off('messenger:message_like', onMessageLike);
    socket.off('messenger:pins_updated', onPinsUpdated);
  };
}
